import { EntityDetailSkeleton } from '@/components/layout/entity-detail-skeleton'
import { BriefSectionEditAction } from '@/components/recruiter/brief-section-edit-action'
import { PersonIdentityCell } from '@/components/person-identity-cell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { formatDistanceToNow } from 'date-fns'
import { Edit, KeyRound, Mail, ShieldX } from 'lucide-react'
import { toast } from 'sonner'
import { useSendUserRecovery, useUser } from '@/hooks/use-admin-users'
import {
  AdminUserAccessBadges,
  AdminUserGroupBadges,
  adminUserToPersonModel,
  formatAdminUserDate,
  formatAdminUserLastLogin,
} from '@/lib/admin-user-display'

interface UserDetailProps {
  userId: string
  onEdit: () => void
  variant?: 'embedded' | 'page'
  onClose?: () => void
}

function AccountRecoveryCard({ user }: { user: NonNullable<ReturnType<typeof useUser>['data']> }) {
  const sendRecovery = useSendUserRecovery()

  const send = () =>
    sendRecovery.mutate(user.id, {
      onSuccess: (result) => toast.success(`Password reset email sent to ${result.sent_to}`),
      onError: (error) => toast.error(error.message),
    })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account recovery</CardTitle>
        <CardDescription>
          Email this person a link to set a new password. You never see or set their password.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {user.last_recovery_at
            ? `Last sent ${formatDistanceToNow(new Date(user.last_recovery_at), { addSuffix: true })}`
            : 'No recovery email sent yet'}
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={!user.is_active || sendRecovery.isPending}>
              <KeyRound className="mr-2 size-4" aria-hidden />
              Send password reset email
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Send password reset email?</AlertDialogTitle>
              <AlertDialogDescription>
                {user.email} will get an email with a link to choose a new password. Their current
                password keeps working until they use it.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={send}>Send email</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}

function UserManagementSections({ user }: { user: NonNullable<ReturnType<typeof useUser>['data']> }) {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Account information</CardTitle>
          <CardDescription>Basic account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-muted-foreground">User ID</label>
              <p className="font-mono text-sm">{user.id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Username</label>
              <p>{user.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            <div>
              <label className="font-medium text-muted-foreground">Joined</label>
              <p>{formatAdminUserDate(user.date_joined)}</p>
            </div>
            <div>
              <label className="font-medium text-muted-foreground">Last login</label>
              <p>{formatAdminUserLastLogin(user.last_login)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Groups &amp; permissions</CardTitle>
          <CardDescription>User groups and role-based access control</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-muted-foreground">Groups</label>
            <AdminUserGroupBadges groups={user.groups} />
          </div>

          <Separator />

          <div>
            <label className="mb-2 block text-sm font-medium text-muted-foreground">Access</label>
            <AdminUserAccessBadges user={user} />
          </div>
        </CardContent>
      </Card>

      <AccountRecoveryCard user={user} />
    </>
  )
}

export function UserDetail({ userId, onEdit, variant = 'embedded', onClose }: UserDetailProps) {
  const { data: user, isLoading } = useUser(userId)

  if (isLoading) {
    return <EntityDetailSkeleton variant="profile" withPageContainer={false} ariaLabel="Loading user" />
  }

  if (!user) {
    return (
      <div className="py-8 text-center">
        <ShieldX className="mx-auto mb-4 size-12 text-muted-foreground" />
        <h3 className="mb-2 text-lg font-semibold">User not found</h3>
        <p className="text-muted-foreground">
          The requested user could not be found or may have been deleted.
        </p>
        {onClose ? (
          <div className="mt-4">
            <Button onClick={onClose}>Close</Button>
          </div>
        ) : null}
      </div>
    )
  }

  const person = adminUserToPersonModel(user)
  const emailSubline = (
    <span className="flex items-center gap-2">
      <Mail className="size-4 shrink-0" aria-hidden />
      {user.email}
    </span>
  )

  return (
    <div className="space-y-6">
      {variant === 'page' ? (
        <Card className="group/section relative z-0">
          <CardHeader showTitle={false}>
            <BriefSectionEditAction label="Edit user" onClick={onEdit} />
          </CardHeader>
          <CardContent>
            <PersonIdentityCell
              size="lg"
              name={person.name}
              subline={emailSubline}
              colorSeed={person.colorSeed}
              header={<AdminUserAccessBadges user={user} />}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-start justify-between">
            <PersonIdentityCell
              size="lg"
              name={person.name}
              nameClassName="text-2xl font-bold"
              subline={emailSubline}
              colorSeed={person.colorSeed}
              footer={<div className="mt-2"><AdminUserAccessBadges user={user} /></div>}
            />

            <Button onClick={onEdit}>
              <Edit className="mr-2 size-4" />
              Edit user
            </Button>
          </div>

          <Separator />
        </>
      )}

      <UserManagementSections user={user} />

      {variant === 'embedded' ? (
        <div className="flex justify-end gap-2">
          {onClose ? (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          ) : null}
          <Button onClick={onEdit}>
            <Edit className="mr-2 size-4" />
            Edit user
          </Button>
        </div>
      ) : null}
    </div>
  )
}
